import React, { useState, useEffect, useMemo } from 'react';
import { Input, Button, Select, Menu as AntMenu, Tooltip, Grid, Table, Popover } from 'antd';
import { InfoCircleOutlined, RightOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import Menu from '../../components/Menu/Menu';
import './Lists.scss';
import {Link} from 'react-router-dom';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import MobileBottomMenu from '../../components/MobileBottomMenu/MobileBottomMenu';
import MobileMenu from "../../components/Menu/MobileMenu";
import Header from '../../components/Header/Header';
import { listService } from "../../services/ListService";
import ListStatusGuard from './ListStatusGuard';
import StatusTag from '../../components/StatusTag/StatusTag';
const { Search } = Input;

const DEVIATION_MAP = {
  missing_data: { label: 'Date lipsă', color: 'gray' },
  anomaly: { label: 'Anomalie', color: 'orange' },
  normal: { label: 'Normal', color: 'green' },
};

const toDisplayValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  return value;
};

const formatMoney = (value, currency = 'RON') => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return '—';
  }
  return `${Number(value)} ${currency}`;
};

const FinalValidation = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [selectedBids, setSelectedBids] = useState({});


  const [products, setProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentList, setCurrentList] = useState(null);

  const [isVerified, setIsVerified] = useState(false);
  const canFinalizeCurrentList = ListStatusGuard.canFinalValidate(currentList?.status);
  const anomalyCount = 0;

  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const normalizedSearchQuery = searchQuery.trim().toLowerCase();

  const filteredProducts = useMemo(() => {
    if (!normalizedSearchQuery) {
      return products;
    }

    return products.filter((item) => {
      const product = item?.product || {};
      const searchableValues = [
        product.name,
        product.barcode,
        product.internal_number,
        item?.id,
      ];

      return searchableValues.some((value) =>
        String(value ?? '').toLowerCase().includes(normalizedSearchQuery)
      );
    });
  }, [products, normalizedSearchQuery]);

  useEffect(() => {
    listService.getFinalValidation(id)
      .then(res => {
        if (res.data) {
          setProducts(res.data.items || []);
          if (res.data.necessity_list) {
            setCurrentList(res.data.necessity_list);
          }
        }
      })
      .catch(err => {
        console.error("Eroare la încărcarea produselor:", err);
      });

    listService.getSpecificList(id)
      .then(res => {
        if (res.data) {
          setCurrentList(res.data);
        }
      })
      .catch(err => {
        console.error("Eroare la încărcarea listei:", err);
      });
  }, []);

  const handleSearch = (value) => {
    setSearchQuery(value);
  };

  const isUnofferedItem = (record) => String(record?.status || '').toLowerCase() === 'unoffered';

  const canRejectItem = (record) => {
    const hasBids = Array.isArray(record?.all_bids) && record.all_bids.length > 0;
    return hasBids && !isUnofferedItem(record);
  };

  const hasOwnSelection = (itemId) => Object.prototype.hasOwnProperty.call(selectedBids, itemId);

  const getBestBidForItem = (itemRecord) => {
    const bids = itemRecord?.all_bids || [];
    if (bids.length === 0) {
      return null;
    }

    return bids.reduce((min, bid) => (bid.price < min.price ? bid : min), bids[0]);
  };

  const getResolvedSelectedBidId = (itemRecord) => {
    if (hasOwnSelection(itemRecord.id)) {
      return selectedBids[itemRecord.id];
    }

    return getBestBidForItem(itemRecord)?.id || null;
  };

  const getPriceDeviationDetailsContent = (details) => {
    if (!details) {
      return <span>Nu există detalii pentru această deviere.</span>;
    }

    const thresholdCurrency = details.threshold_currency || 'RON';
    const currentOfferSupplier = details?.current_offer?.supplier?.name || 'Necunoscut';
    const currentOfferPrice = formatMoney(details?.current_offer?.price, thresholdCurrency);
    const referenceSupplier = details?.reference_purchase?.supplier?.name || 'Necunoscut';
    const referenceSource = details?.reference_purchase?.source || 'Nespecificat';
    const referencePrice = formatMoney(details?.reference_purchase?.purchase_price, thresholdCurrency);
    const referenceDate = details?.reference_purchase?.purchased_at
      ? moment(details.reference_purchase.purchased_at).format('DD.MM.YYYY')
      : '—';
    const percentDiff = details?.difference_percent;

    return (
      <div style={{ maxWidth: 340 }}>
        <div><strong>Status:</strong> {toDisplayValue(details.status)}</div>
        <div><strong>Prag:</strong> {formatMoney(details.threshold_value, thresholdCurrency)}</div>
        <div><strong>Diferență absolută:</strong> {formatMoney(details.absolute_difference, thresholdCurrency)}</div>
        <div><strong>Diferență procentuală:</strong> {percentDiff === null || percentDiff === undefined ? '—' : `${Number(percentDiff)}%`}</div>
        <div><strong>Oferta curentă:</strong> {currentOfferSupplier} - {currentOfferPrice}</div>
        <div><strong>Referință istorică:</strong> {referenceSupplier} - {referencePrice}</div>
        <div><strong>Sursă referință:</strong> {referenceSource}</div>
        <div><strong>Data referinței:</strong> {referenceDate}</div>
      </div>
    );
  };

  const renderPriceDeviationBadge = (value, details, trigger = 'hover') => {
    const deviation = DEVIATION_MAP[value];
    if (!deviation) {
      return '—';
    }

    const badge = (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <span
          style={{
            display: 'inline-block',
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: deviation.color,
          }}
        />
        {deviation.label}
        {details ? <InfoCircleOutlined style={{ color: '#1890ff' }} /> : null}
      </span>
    );

    if (!details) {
      return badge;
    }

    return (
      <Popover
        title="Detalii deviere de preț"
        content={getPriceDeviationDetailsContent(details)}
        trigger={trigger}
      >
        <span style={{ cursor: 'pointer' }}>{badge}</span>
      </Popover>
    );
  };



  const listColumns = [
    {
      title: 'Nume produs',
      dataIndex: ['product', 'name'],
      key: 'title'
    },
    {
      title: 'Creată la',
      dataIndex: ['product', 'created_at'],
      key: 'created_at',
      width: 140,
      render: (value) => moment(value).format('DD.MM.YYYY'),
    },
    {
      title: 'Top ofertă',
      dataIndex: 'all_bids',
      key: 'all_bids',
      width: 160,
      render: (bids, record) => {
        const selectedBidId = getResolvedSelectedBidId(record);

        return bids?.length > 0 ? (
          <Select
            value={selectedBidId ?? undefined}
            style={{ width: '100%' }}
            onChange={(value) => {
              setSelectedBids((prev) => ({ ...prev, [record.id]: value }));
            }}
          >
            {bids.map((bid) => (
              <Select.Option key={bid.id} value={bid.id}>
                {bid.supplier?.name || 'Necunoscut'} - {bid.price} RON
              </Select.Option>
            ))}
          </Select>
        ) : (
          <Select disabled placeholder="Fără oferte" style={{ width: '100%' }} />
        );
      }
    },
    {
      title: 'Top ofertă din istoric',
      key: 'lowest_historical_price',
      width: 160,
      render: (_, record) => {
        const prices = record.historical_prices || [];
        const lowest = prices.reduce((min, item) =>
          item.purchase_price < min.purchase_price ? item : min,
          prices[0] || {}
        );

        const tooltipContent = (
          <div>
            {prices.map((p, idx) => (
              <div key={idx}>
                {p.supplier?.name} - {p.purchase_price} RON ({moment(p.purchased_at).format('DD.MM.YYYY')})
              </div>
            ))}
          </div>
        );

        return prices.length ? (
          <Tooltip title={tooltipContent}>
            <span style={{ cursor: 'help' }}>
              {lowest.purchase_price} RON <InfoCircleOutlined style={{ color: '#1890ff' }} />
            </span>
          </Tooltip>
        ) : (
          '—'
        );
      },
    },
    {
      title: 'Devieri de preț',
      dataIndex: 'price_deviations',
      key: 'price_deviations',
      width: 160,
      render: (value, record) => {
        return renderPriceDeviationBadge(value, record?.price_deviation_details, 'hover');
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status) => <StatusTag status={status} />,
    },
    {
      title: 'Acțiuni',
      key: 'actions',
      width: 180,
      render: (_, record) => {
        if (!canRejectItem(record)) {
          return null;
        }

        return (
          <Button
            type="default"
            danger
            onClick={() => handleReject(record)}
          >
            Respinge
          </Button>
        );
      },
    },
  ];

  const handleReject = (record) => {
    if (!canRejectItem(record)) {
      return;
    }

    setSelectedBids((prev) => ({
      ...prev,
      [record.id]: null
    }));
    toast.info(`Produsul "${record?.product?.name || 'Necunoscut'}" a fost marcat ca respins.`);
  };

  const handleFinalVerification = () => {
    if (!canFinalizeCurrentList) {
      return;
    }

    const payload = {
      items: products.map((product) => {
        const selectedBidId = getResolvedSelectedBidId(product);
        return {
          id: product.id,
          selected_bid_id: selectedBidId
        };
      })
    };

    listService.finalizeValidation(id, payload)
      .then(() => {
        toast.success('Verificare finalizată cu succes');
        setIsVerified(true);
        navigate('/liste-ofertate', { replace: true });
      })
      .catch(() => {
        toast.error('Eroare la finalizarea verificării');
      });
  };
  const MobileList = () => {
    const dataSource = filteredProducts;

    if (dataSource.length === 0) {
      return (
        <div className="list__empty-state">
          <p className="list__empty-state-text">Nu există produse pentru validare.</p>
        </div>
      );
    }

    return (
      <div className="list__mobile-table-wrapper">
        <div className="list__mobile-table-header">
          <div className="list__mobile-table-header-cell list__mobile-table-header-name">Produs</div>
          <div className="list__mobile-table-header-cell" style={{ width: '120px', flexShrink: 0 }}>Status</div>
          <div className="list__mobile-table-header-cell list__mobile-table-header-actions" style={{ width: '80px', flexShrink: 0 }}>Opt</div>
        </div>
        <div className="list__mobile-list">
          {dataSource.map((row) => {
            const hasBids = Array.isArray(row?.all_bids) && row.all_bids.length > 0;
            const canReject = canRejectItem(row);
            return (
              <div key={row.id} className="list__mobile-card">
                <div className="list__mobile-card-cell list__mobile-card-name" title={row.product?.name}>
                  {row.product?.name}
                </div>
                <div className="list__mobile-card-cell" style={{ width: '120px', flexShrink: 0 }}>
                  <StatusTag status={row.status} />
                  <div style={{ marginTop: 6, fontSize: 12 }}>
                    {renderPriceDeviationBadge(row?.price_deviations, row?.price_deviation_details, 'click')}
                  </div>
                </div>
                <div className="list__mobile-card-cell list__mobile-card-actions" style={{ width: '80px', flexShrink: 0 }}>
                  {canReject ? (
                    <Button type="default" danger size="small" onClick={() => handleReject(row)}>
                      Respinge
                    </Button>
                  ) : (
                    null
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className='standard products list'>
      <Header />
      <div className='standard__mobile-header'>
        <img src='/images/logo.png' alt='logo' className='standard__only-mobile standard__mobile-logo' />
        <MobileMenu />
      </div>

      <div className='standard__container list'>
        <Menu />
        <div className='standard__wrapper'>
          <div style={{ padding: 24, minHeight: 280 }}>
            <h2 className='standard__page-title'>Liste ofertate/Verificare Finala/Lista nr {id}</h2>
            <h4 className='standard__page-info'>
              Aici urmaresti listele ofertate, poti extrage produsele fara oferte si analiza usor optiunile primite.
            </h4>
            <Link to='/liste-ofertate' className='standard__link'>
              <div className='biding__back'>
                <img src="/images/back.png" />
                <h4>Înapoi</h4>
              </div>
            </Link>
            <div className='list__search-box'>
              <Search
                className="list__search-input"
                placeholder="Caută produs în lista curentă..."
                onChange={(e) => handleSearch(e.target.value)}
                value={searchQuery}
                enterButton
                allowClear
                style={{ width: 300 }}
              />

            </div>



            <div className="list__final-validation-layout">
              <div className="list__final-validation-content">
              {!isMobile ? (
                <Table
                  dataSource={filteredProducts}
                  columns={listColumns}
                  rowKey="id"
                  locale={{
                    emptyText: 'Nu există produse care corespund căutării.'
                  }}
                  pagination={false}
                  scroll={{ x: true }}
                />
              ) : (
                <MobileList />
              )}
              </div>
              {isMobile && (
                <div className="list__final-validation-sticky-footer">
                  <Button
                    type="primary"
                    icon={<RightOutlined />}
                    className="list__final-validation-action-button"
                    onClick={handleFinalVerification}
                    disabled={!canFinalizeCurrentList || isVerified}
                  >
                    {isVerified ? 'Listă trimisă' : 'Generează și trimite lista'}
                  </Button>
                </div>
              )}
            </div>

            {!isMobile && (
              <div className="list__table-footer list__final-validation-desktop-footer">
                <h4 className="list__table-footer-info">
                  Anomalii identificate: <strong>{anomalyCount}</strong>
                </h4>
                <Button
                  type="primary"
                  icon={<RightOutlined />}
                  className="list__final-validation-action-button"
                  onClick={handleFinalVerification}
                  disabled={!canFinalizeCurrentList || isVerified}
                >
                  {isVerified ? 'Listă trimisă' : 'Generează și trimite lista'}
                </Button>
              </div>
            )}


          </div>
        </div>
      </div>

      <MobileBottomMenu />

    </div>
  );
};

export default FinalValidation;